import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

function getPriceByKey(data, key) {
  if (!data) return null;
  const item = data.find((d) => d.key === key);
  return item ? item.value : null;
}

export default function PricingSection({ data }) {
  const { t } = useTranslation();

  const basePrice = getPriceByKey(data, "base_price_48h") ?? 12;
  const addon7d = getPriceByKey(data, "addon_7d") ?? 6;
  const addon14d = getPriceByKey(data, "addon_14d") ?? 14;
  const addonRoundTrip = getPriceByKey(data, "addon_round_trip") ?? 5;
  const addonActivateLater = getPriceByKey(data, "addon_activate_later") ?? 1;

  const tiers = [
    {
      name: t("pricing.standard"),
      price: basePrice,
      validity: t("pricing.validity_48h"),
      popular: false,
      features: [
        t("pricing.feature_passenger"),
        t("pricing.feature_pdf"),
        t("pricing.feature_email"),
        t("pricing.feature_reference"),
      ],
    },
    {
      name: t("pricing.popular"),
      price: basePrice + addon7d,
      validity: t("pricing.validity_7d"),
      popular: true,
      features: [
        t("pricing.feature_passenger"),
        t("pricing.feature_pdf"),
        t("pricing.feature_email"),
        t("pricing.feature_reference"),
        t("pricing.feature_extended"),
      ],
    },
    {
      name: t("pricing.premium"),
      price: basePrice + addon14d,
      validity: t("pricing.validity_14d"),
      popular: false,
      features: [
        t("pricing.feature_passenger"),
        t("pricing.feature_pdf"),
        t("pricing.feature_email"),
        t("pricing.feature_reference"),
        t("pricing.feature_extended"),
        t("pricing.feature_priority"),
      ],
    },
  ];

  return (
    <section id="pricing" className="py-24 md:py-32 bg-[#f8fafc]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-[#1e3a5f]">
            {t("pricing.title")}
          </h2>
        </motion.div>

        {!data ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-[#e2e8f0] rounded-2xl p-8">
                <Skeleton className="h-6 w-24 mb-4" />
                <Skeleton className="h-12 w-20 mb-6" />
                <Skeleton className="h-4 w-full mb-3" />
                <Skeleton className="h-4 w-full mb-3" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="pricing-tiers">
              {tiers.map((tier, i) => (
                <motion.div
                  key={tier.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className={`relative bg-white rounded-2xl p-8 flex flex-col hover:shadow-lg hover:scale-[1.02] transition-all duration-300 ${
                    tier.popular
                      ? "border-2 border-[#f97316] shadow-md"
                      : "border border-[#e2e8f0]"
                  }`}
                  data-testid={`pricing-tier-${i}`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-[#f97316] text-white text-xs font-semibold px-4 py-1.5 rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {t("pricing.most_popular")}
                      </span>
                    </div>
                  )}

                  <h3 className="font-['Outfit'] text-lg font-semibold text-[#1e3a5f] mb-1">
                    {tier.name}
                  </h3>
                  <p className="text-xs text-[#6b7280] mb-4">{tier.validity}</p>

                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-[#1e3a5f] font-['Outfit']">
                      ${tier.price.toFixed(0)}
                    </span>
                    <span className="text-sm text-[#6b7280]">/{t("pricing.per_ticket")}</span>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {tier.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-2.5 text-sm text-[#1f2937]">
                        <Check className="w-4 h-4 text-[#22c55e] shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`w-full py-3 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5 ${
                      tier.popular
                        ? "bg-[#f97316] text-white hover:bg-[#ea580c] hover:shadow-lg"
                        : "bg-[#1e3a5f] text-white hover:bg-[#152e4d] hover:shadow-lg"
                    }`}
                    data-testid={`pricing-cta-${i}`}
                  >
                    {t("pricing.get_started")}
                  </button>
                </motion.div>
              ))}
            </div>

            {/* Add-ons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-12 text-center"
              data-testid="pricing-addons"
            >
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#1e3a5f] mb-4">
                {t("pricing.addons_title")}
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                <span className="bg-white border border-[#e2e8f0] rounded-xl px-5 py-2.5 text-sm text-[#1f2937]">
                  {t("pricing.addon_round_trip")} <span className="font-semibold text-[#f97316]">+${addonRoundTrip.toFixed(0)}</span>
                </span>
                <span className="bg-white border border-[#e2e8f0] rounded-xl px-5 py-2.5 text-sm text-[#1f2937]">
                  {t("pricing.addon_activate_later")} <span className="font-semibold text-[#f97316]">+${addonActivateLater.toFixed(0)}</span>
                </span>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
}
